import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box } from "@mui/system";
import Typography from "@mui/material/Typography";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  InputLabel,
  TableCell,
  TextField,
} from "@mui/material";
import DeleteForeverSharpIcon from "@mui/icons-material/DeleteForeverSharp";
import { useNavigate, useParams } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import "./style.css";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch, useSelector } from "react-redux";
import { fetchSTEP3Request } from "../../actions/step3Actions";
import { fetchGetStep1Request } from "../../actions/getStep1Actions";
import { getStep1Details } from "../../reducers/getStep1Reducer";
import { toast } from "react-toastify";
import { getSTEP3EnteredDetails, getSTEP3Pending } from "../../reducers/step3Reducer";
import FileViewer from "react-file-viewer";
import FilePreview from "react-file-preview-latest";
import { PictureAsPdf, Image, Preview } from "@mui/icons-material";
import ArticleIcon from "@mui/icons-material/Article";
import { useFormik } from "formik";
import ReactHtmlParser from "html-react-parser";
import StorageService from "../../services/Storage.service";
import Moment from "moment";
import { getFilesViewDetails } from "../../reducers/viewFileReducer";
import { fetchFilesViewRequest } from "../../actions/viewFile.Actions";
import { fetchDeleteFilesRequest } from "../../actions/deleteFileActions";
import { getDelFilesDetails } from "../../reducers/deleteFileReducer";

// Only these types are allowed for Step 3 upload — nothing else.
const ALLOWED_EXTENSIONS = ["pdf", "doc", "docx", "jpg", "jpeg", "png"] as const;
const ALLOWED_MIME_BY_EXT: Record<(typeof ALLOWED_EXTENSIONS)[number], string[]> = {
  pdf: ["application/pdf", "application/octet-stream"],
  doc: ["application/msword", "application/octet-stream"],
  docx: [
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/octet-stream",
    "application/zip", // some browsers report docx as zip
  ],
  jpg: ["image/jpeg", "image/jpg", "application/octet-stream"],
  jpeg: ["image/jpeg", "image/jpg", "application/octet-stream"],
  png: ["image/png", "application/octet-stream"],
};

const FILE_TYPE_ERROR =
  "Only PDF (.pdf), Word (.doc, .docx), and Images (.jpg, .jpeg, .png) are allowed";
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 🔧 fixed: 10MB guard to avoid opaque server failures
const ACCEPT_ATTR = ".pdf,.doc,.docx,.jpg,.jpeg,.png";

/** File Name (*) — remove commas only (silent; do not rewrite the input while typing) */
const sanitizeDisplayFileName = (value: string) => value.replace(/,/g, "");

/**
 * Uploaded OS file base name — original behavior:
 * keep letters/numbers only (strip spaces, dashes, underscores, special chars).
 */
const sanitizeUploadedOsFileName = (value: string) =>
  value.replace(/[^A-Z0-9]+/gi, "");

interface PendingFile {
  fileName: string;
  file: File;
}

const isAllowedUploadFile = (file: File): boolean => {
  const ext = (file.name.split(".").pop() || "").toLowerCase();
  if (!(ALLOWED_EXTENSIONS as readonly string[]).includes(ext)) {
    return false;
  }
  // Extension is the source of truth. Empty MIME is common and OK.
  // Only reject when browser reports a MIME that clearly conflicts with the allowlist.
  if (!file.type) {
    return true;
  }
  const allowedMimes =
    ALLOWED_MIME_BY_EXT[ext as (typeof ALLOWED_EXTENSIONS)[number]];
  return allowedMimes.includes(file.type);
};

interface UploadDocumentationProps {
  set?: (dirty: boolean) => void;
}

const UploadDocumentation = ({ set }: UploadDocumentationProps) => {
  // 🔧 fixed: correct props signature (was (props, { token }) which never receives token)
  const markDirty = useCallback(() => {
    set?.(true);
  }, [set]);

  const employerNameCoki = StorageService.getCookies("employerUrl");
  const params = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [file, setFile] = useState<File | string | Record<string, never>>({});
  const [fileType, setFileType] = useState("");
  const date = Moment().format("MM-DD-YYYY");
  const [isSubmitted, setSubmitted] = useState(false);
  const [preview, setPreview] = useState(false);
  const [children, setChildren] = useState<PendingFile[]>([]);
  const [fileName, setFileName] = useState("");
  const [userFile, setUserFile] = useState<File | null>(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [open, setOpen] = useState(false);
  const [roleId, setRoleId] = useState(0);
  const [pre, setPre] = useState(false);

  const ref = useRef<HTMLInputElement | null>(null);
  const step1Res: any = useSelector(getStep1Details);
  const step3Res: any = useSelector(getSTEP3EnteredDetails);
  const uploadPending = useSelector(getSTEP3Pending);
  const fileRes: any = useSelector(getFilesViewDetails);
  const fileDataAfterDel: any = useSelector(getDelFilesDetails);
  const result = step1Res?.uploadedDocList;

  const getStep1ByEventId = useCallback(() => {
    const eventToken = StorageService.getCookies("eventToken");
    const token: any = params.token ? params.token : eventToken;
    if (token && token !== "0") {
      dispatch(fetchGetStep1Request({ token }));
    }
  }, [dispatch, params.token]);

  useEffect(() => {
    getStep1ByEventId();
  }, [getStep1ByEventId]);

  const getExtension = (name: string) =>
    name.split(".").pop()?.toLowerCase() || ""; // 🔧 fixed: use last segment (split(".")[1] broke multi-dot names)

  const clearFileInput = () => {
    if (ref.current) {
      ref.current.value = "";
    }
  };

  const handleFileChoose = (ev: React.ChangeEvent<HTMLInputElement>) => {
    const selected = ev.target.files?.[0];
    if (!selected) {
      setUserFile(null);
      return;
    }

    // Original OS-file sanitize: alphanumeric only (silent — picker still shows original name)
    const parts = selected.name.split(".");
    const extension = parts.pop() || "";
    const baseName = sanitizeUploadedOsFileName(parts.join(""));

    if (baseName.length <= 1) {
      setErrorMsg("Invalid file name");
      setUserFile(null);
      clearFileInput();
      return;
    }

    // Reject anything outside PDF / Word / jpg|jpeg|png
    if (!isAllowedUploadFile(selected)) {
      setErrorMsg(FILE_TYPE_ERROR);
      setUserFile(null);
      clearFileInput();
      return;
    }

    if (selected.size > MAX_FILE_SIZE_BYTES) {
      setErrorMsg("File must be 10MB or smaller");
      setUserFile(null);
      clearFileInput();
      return;
    }

    const newFile = new File([selected], `${baseName}.${extension}`, {
      type: selected.type,
    });
    setErrorMsg("");
    setUserFile(newFile);
    markDirty();
  };

  const addChild = () => {
    const displayName = sanitizeDisplayFileName(fileName.trim());
    if (!userFile || !displayName) {
      return;
    }

    if (!isAllowedUploadFile(userFile)) {
      toast.error(FILE_TYPE_ERROR);
      setUserFile(null);
      clearFileInput();
      return;
    }

    setChildren((prev) => [
      ...prev,
      {
        fileName: displayName, // commas removed silently
        file: userFile,
      },
    ]);
    setFileName("");
    clearFileInput();
    setUserFile(null);
    markDirty();
  };

  const handleDelete = (index: number) => {
    setChildren((prev) => prev.filter((_, i) => i !== index));
    setPreview(false);
    markDirty();
  };

  const previewFile = (fileItems: PendingFile) => {
    // 🔧 fixed: removed debugger
    const fileExt = getExtension(fileItems.file.name);
    let previewTarget: File | string = fileItems.file;
    if (fileExt === "doc" || fileExt === "docx") {
      previewTarget = URL.createObjectURL(fileItems.file);
    }
    markDirty();
    setFile(previewTarget);
    setFileType(fileExt);
    setPreview(true);
  };

  const closePreview = () => {
    setPreview(false);
    markDirty();
  };

  const onError = (_error: unknown) => {
    // 🔧 fixed: no console.log of preview errors in production path
    toast.error("Unable to preview this file");
  };

  const formik = useFormik({
    initialValues: {},
    enableReinitialize: true,
    onSubmit: () => {
      setSubmitted(true);
      if (!children.length) {
        return;
      }
      // Final guard: never send disallowed types to step3Api
      const invalid = children.filter((item) => !isAllowedUploadFile(item.file));
      if (invalid.length > 0) {
        toast.error(FILE_TYPE_ERROR);
        setChildren((prev) => prev.filter((item) => isAllowedUploadFile(item.file)));
        return;
      }
      // 🔧 fixed: guard missing eventId with user-visible error (was silent no-op)
      if (!(step1Res && step1Res.eventId > 0)) {
        toast.error("Event details are not loaded yet. Please refresh and try again.");
        return;
      }

      const formData = new FormData();
      formData.append("eventId", step1Res.eventId.toString());
      for (const item of children) {
        formData.append("fileName", sanitizeDisplayFileName(item.fileName));
        formData.append("file", item.file, item.file.name);
      }
      dispatch(fetchSTEP3Request({ formData }));
    },
  });

  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (fileRes && pre) {
      setPre(false);
      const blob = new Blob([fileRes], { type: fileRes.type });
      const fileURL = URL.createObjectURL(blob);
      window.open(fileURL, "_blank");
      // 🔧 fixed: revoke object URL after open to avoid leaks (best-effort)
      setTimeout(() => URL.revokeObjectURL(fileURL), 60_000);
    }
  }, [fileRes, pre]);

  const fileView = (fileNameToView: string) => {
    if (!step1Res?.eventId) {
      toast.error("Event details are not loaded yet.");
      return;
    }
    const id = step1Res.eventId.toString();
    dispatch(fetchFilesViewRequest({ id, fileName: fileNameToView }));
    setPre(true);
  };

  const handleRemove = (Id: number) => {
    if (!step1Res?.eventId) {
      toast.error("Event details are not loaded yet.");
      return;
    }
    setSubmitted(true);
    const id = step1Res.eventId.toString();
    dispatch(fetchDeleteFilesRequest({ id, fileId: Id }));
    handleClose();
  };

  const lastDeleteHandled = useRef<unknown>(null);
  useEffect(() => {
    // 🔧 fixed: do not mutate Redux state; dedupe toasts via ref instead
    if (!fileDataAfterDel || fileDataAfterDel === lastDeleteHandled.current) {
      return;
    }
    if (fileDataAfterDel.isSuccess === true) {
      lastDeleteHandled.current = fileDataAfterDel;
      toast.success("Deleted Successfully");
      getStep1ByEventId();
    } else if (fileDataAfterDel.isSuccess === false) {
      lastDeleteHandled.current = fileDataAfterDel;
      const msg =
        fileDataAfterDel.errorMessages?.[0] || "Delete failed. Please try again.";
      toast.error(msg);
    }
  }, [fileDataAfterDel, getStep1ByEventId]);

  useEffect(() => {
    if (!step3Res) {
      return;
    }
    // 🔧 fixed: treat isSuccess OR uploadedFileList as success; always toast API errors
    if (
      step3Res.isSuccess === true ||
      (step3Res.uploadedFileList && step3Res.uploadedFileList.length > 0)
    ) {
      toast.success("Files uploaded successfully");
      navigate(`/${employerNameCoki}/form-thankyou/PendingReview`);
    } else if (step3Res.isSuccess === false) {
      const msg =
        step3Res.errorMessages?.[0] || "Upload failed. Please try again.";
      toast.error(msg);
    }
  }, [step3Res, employerNameCoki, navigate]);

  const documents: string[] = Array.isArray(step1Res?.documents)
    ? step1Res.documents
    : []; // 🔧 fixed: avoid crash when documents is undefined

  return (
    <form onSubmit={formik.handleSubmit}>
      <Box>
        <table>
          <tbody>
            <tr>
              <th className="text-muted">Name:</th>
              <td>{step1Res?.name}</td>
            </tr>
            <tr>
              <th className="text-muted">Event Type:</th>
              <td>{step1Res?.eventType}</td>
            </tr>
            <tr>
              <th className="text-muted">Sub-Event Type:</th>
              <td>{step1Res?.eventSubType}</td>
            </tr>
            <tr>
              <th className="text-muted" style={{ textAlign: "inherit" }}>
                Confirmation Number:
              </th>
              <td>{step1Res?.confirmationNumber}</td>
            </tr>
          </tbody>
        </table>

        <Typography className="para">
          Thanks for initiating your QLE and telling us about the changes you
          want to make to your benefits. Now we need some supporting
          documentation for our records. Remember, to be eligible to make
          changes to your benefits related to your qualifying life event, you
          must submit your documentation within 60 days of the event. Your
          documentation must provide proof of the qualifying event, including
          the date of the event.
        </Typography>

        <Typography
          className="para"
          style={{ position: "relative", bottom: "9px" }}
        >
          Based on your Event Type and the changes you have requested to your
          benefits, please submit any one of the following documents:
        </Typography>

        {documents.map((item: string, key: number) => (
          // 🔧 fixed: prefer html-react-parser over dangerouslySetInnerHTML for consistency
          // NOTE: still trusts CMS HTML — sanitize with DOMPurify when available
          <p key={`doc-${key}`}>{ReactHtmlParser(item || "")}</p>
        ))}

        <Typography
          className="para"
          style={{ position: "relative", bottom: "28px" }}
        >
          <p className="para2">
            {ReactHtmlParser(step1Res?.step3DocumentCovered || "")}
          </p>
        </Typography>

        <Typography
          className="para"
          style={{ position: "relative", bottom: "44px" }}
        >
          When you have your documentation ready, use the button below to upload
          it. You may upload multiple documents. If we don&apos;t receive supporting
          documentation within 60 days of your event date, we will automatically
          close this qualifying life event submission.
        </Typography>

        <Typography variant="h6" sx={{ margin: "-34px 0px 0px 0px" }}>
          Upload Documentation
        </Typography>

        <div
          className={
            children.length === 0 && isSubmitted ? "error-span doc" : "error-span"
          }
          role="alert"
          aria-live="polite"
        >
          {children.length === 0 && isSubmitted
            ? "Please add atleast one file"
            : ""}
        </div>

        <Grid container sx={{ marginTop: "20px" }}>
          <Grid item xs={12} md={4}>
            <TextField
              id="fileName"
              label="File Name (*)"
              variant="outlined"
              name="displayFileName"
              fullWidth
              value={fileName}
              onChange={(ev) => {
                setFileName(ev.target.value);
                if (ev.target.value) {
                  markDirty();
                }
              }}
            />
          </Grid>

          <Grid item xs={12} md={5} className="choose_file">
            <TextField
              id="supportingDoc"
              variant="outlined"
              sx={{ display: "block" }}
              inputProps={{ accept: ACCEPT_ATTR }}
              name="file"
              type="file"
              className="align-upload"
              fullWidth
              inputRef={ref}
              onChange={handleFileChoose}
            />
            <InputLabel
              htmlFor="supportingDoc"
              style={{
                fontSize: "0.95rem",
                display: "block",
                whiteSpace: "normal",
              }}
            >
              Allowed files: PDF (.pdf), Word (.doc, .docx), Images (.jpg, .jpeg,
              .png).
            </InputLabel>
            {errorMsg && (
              <span style={{ color: "red", fontSize: "12px" }} role="alert">
                {errorMsg}
              </span>
            )}
          </Grid>

          <Grid item xs={12} md={2} className="newbtn1">
            <Button
              style={{ marginLeft: "23px" }}
              fullWidth
              className="align-upload"
              type="button"
              sx={{
                color: "#6c757d",
                marginLeft: "20px",
                border: "1px solid gray",
                padding: "13px",
                "&:hover": {
                  background: "#6c757d",
                  color: "#ffff",
                },
              }}
              disabled={!fileName.trim() || !userFile}
              onClick={addChild}
            >
              <AddIcon />
              <Typography sx={{ padding: "5px 0px 0px 6px", fontSize: "16px" }}>
                ADD
              </Typography>
            </Button>
            <InputLabel
              style={{
                fontSize: "0.95rem",
                marginLeft: "25px",
                whiteSpace: "normal",
              }}
            >
              Select +ADD to upload your document
            </InputLabel>
          </Grid>
        </Grid>

        <Grid
          container
          md={7}
          sx={{ margin: "20px 2px 0px 2px" }}
          className="header_width"
        >
          <Grid item md={1} className="bx-1" xs={1} />
          <Grid item md={5} className="bx-1" xs={6}>
            <Typography sx={{ fontWeight: "600" }}>File Name</Typography>
          </Grid>
          <Grid item md={2} className="bx-1" xs={5}>
            <Typography sx={{ fontWeight: "600" }}>Preview</Typography>
          </Grid>
          <Grid item md={4} className="bx-1" xs={1}>
            <Typography sx={{ fontWeight: "600", marginLeft: "45px" }}>
              Date{" "}
            </Typography>
          </Grid>
        </Grid>

        {result &&
          result.map((item: any) => (
            <Grid
              container
              md={7}
              sx={{ margin: "2px 2px 0px 2px" }}
              className="files-width"
              key={item.id ?? item.fileName}
            >
              <Grid item md={1} className="bx-1" xs={1}>
                <PictureAsPdf />
              </Grid>
              <Grid item md={5} className="bx-1" xs={4} id="fileName">
                <Typography sx={{ padding: "1px 0px" }}>
                  {item.documentName}
                </Typography>
              </Grid>
              <Grid item md={2} className="bx-1" xs={4}>
                <Typography
                  className="text-red"
                  sx={{ padding: "1px 0px", cursor: "pointer" }}
                  component="button"
                  type="button"
                  onClick={() => fileView(item.fileName)}
                  aria-label={`Preview ${item.documentName}`}
                >
                  <Preview />
                </Typography>
              </Grid>
              <Grid item md={3} className="bx-1" xs={4}>
                <Typography sx={{ padding: "1px 0px" }}>
                  {item.createdDate}
                </Typography>
              </Grid>
              <Grid md={1} mt={1}>
                <TableCell>
                  <Button
                    variant="text"
                    sx={{
                      border: "none",
                      width: "20px",
                      padding: "0px",
                      marginTop: "-5px",
                    }}
                    onClick={() => {
                      setRoleId(item.id);
                      setOpen(true);
                    }}
                    aria-label={`Delete ${item.documentName}`}
                  >
                    <DeleteForeverSharpIcon
                      style={{ marginTop: "-15px" }}
                      color="primary"
                    />
                  </Button>
                </TableCell>
              </Grid>
            </Grid>
          ))}

        {/* 🔧 fixed: one Dialog outside the map (was N dialogs sharing one open state) */}
        <Dialog
          open={open}
          keepMounted
          onClose={handleClose}
          aria-describedby="alert-dialog-slide-description"
          fullWidth
        >
          <DialogTitle>
            <Typography
              variant="caption"
              style={{
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <Typography
                variant="h5"
                sx={{ fontSize: "18px", fontWeight: "600" }}
              >
                Confirmation
              </Typography>
              <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={{
                  position: "absolute",
                  right: 8,
                  top: 8,
                  color: (theme) => theme.palette.grey[500],
                }}
              >
                <CloseIcon />
              </IconButton>
            </Typography>
          </DialogTitle>
          <DialogContent>
            <DialogContentText
              sx={{ fontSize: "17px" }}
              id="alert-dialog-slide-description"
            >
              Sure to Delete?
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ padding: "12px 19px" }}>
            <Button className="cancel-btn-role" onClick={handleClose}>
              No
            </Button>
            <Button
              className="save-btn-role"
              onClick={() => handleRemove(roleId)}
            >
              Yes
            </Button>
          </DialogActions>
        </Dialog>

        {children.map((fileItem, index) => (
          <Grid
            container
            md={7}
            sx={{ margin: "2px 2px 0px 2px" }}
            key={`${fileItem.fileName}-${index}`}
          >
            <Grid item md={1} className="bx-1" xs={1}>
              {fileItem.file.type.indexOf("image") >= 0 ? (
                <Image />
              ) : fileItem.file.type.indexOf("doc") >= 0 ? (
                <ArticleIcon />
              ) : (
                <PictureAsPdf />
              )}
            </Grid>
            <Grid item md={5} className="bx-1" xs={4}>
              <Typography sx={{ padding: "1px 0px" }}>
                {fileItem.fileName}
              </Typography>
            </Grid>
            <Grid item md={2} className="bx-1" xs={4}>
              <Typography
                className="text-red"
                sx={{ padding: "1px 0px", cursor: "pointer" }}
                component="button"
                type="button"
                onClick={() => previewFile(fileItem)}
                aria-label={`Preview ${fileItem.fileName}`}
              >
                <Preview />
              </Typography>
            </Grid>
            <Grid item md={3} className="bx-1" xs={4}>
              <Typography sx={{ padding: "1px 0px" }}>{date}</Typography>
            </Grid>
            <Grid item md={1} className="bx-1" xs={1}>
              <DeleteForeverSharpIcon
                onClick={() => handleDelete(index)}
                sx={{ color: "red", cursor: "pointer" }}
                aria-label={`Remove ${fileItem.fileName}`}
              />
            </Grid>
          </Grid>
        ))}

        {preview && (
          <Button
            className="align-upload"
            type="button"
            sx={{
              marginTop: "20px",
              color: "#6c757d",
              "&:hover": {
                background: "#cccccc",
                color: "#555555",
              },
            }}
            onClick={closePreview}
          >
            <Typography sx={{ padding: "5px 0px 0px 0px", fontSize: "12px" }}>
              Close Preview
            </Typography>
          </Button>
        )}

        {preview && (
          <div
            style={{
              height: "800px",
              width:
                fileType === "pdf" || fileType === "doc" || fileType === "docx"
                  ? "100%"
                  : "424px",
              marginTop: "20px",
            }}
          >
            {fileType === "doc" || fileType === "docx" ? (
              <FileViewer
                fileType={fileType}
                filePath={file}
                onError={onError}
              />
            ) : (
              <FilePreview
                width={
                  fileType === "pdf" ||
                  fileType === "doc" ||
                  fileType === "docx"
                    ? "100%"
                    : "424px"
                }
                type={"file"}
                file={file}
                onError={onError}
              />
            )}
          </div>
        )}

        <Typography className="para">
          <span className="text-red">IMPORTANT NOTE:</span>
          {step1Res?.step3ImportantNote && (
            <p>{ReactHtmlParser(step1Res.step3ImportantNote)}</p>
          )}
        </Typography>

        <hr />
      </Box>

      <Button
        type="submit"
        disabled={uploadPending} // 🔧 fixed: prevent double-submit while upload in flight
        sx={{
          backgroundColor: "red",
          "&:hover": {
            backgroundColor: "orange",
          },
        }}
        variant="contained"
        className="flex-end btn-bg-red"
      >
        {uploadPending ? "Submitting..." : "Submit"}
      </Button>
    </form>
  );
};

export default UploadDocumentation;
